import AvatarLegend from "@/partials/avatars/AvatarLegend";
import { NoAvatarsFound } from "@/partials/avatars/NoAvatarsFound";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { allAvatarsQueryOptions, allCoursesQueryOptions } from "@vixi/api";
import { useTranslation } from "@vixi/localization";
import { RouteOptionsById, RouterIds } from "@vixi/router";
import { Avatar } from "@vixi/types";
import {
  Autocomplete,
  AvatarGridItem,
  FieldLabel,
  Img,
  LoadingScreen,
  PageMainContent,
  useGoBack,
  useUiStore,
} from "@vixi/ui";
import { useDebounce } from "@vixi/utils";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { avatars_per_query } from "@vixi/ui";

// Constants
const routeId = "/_app/avatars/" satisfies RouterIds;

export const AvatarIndexPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: async ({ context: { queryClient } }) => {
    queryClient.ensureQueryData(allAvatarsQueryOptions(1, avatars_per_query));
    queryClient.ensureQueryData(allCoursesQueryOptions({ pageSize: 20, pageNumber: 1 })); // Preload courses for the filter
  },
};

// Course option type for the filter
type CourseOption = { id: number; title: string };
interface AvatarFilterFormValues {
  searchText?: string;
}

function RouteComponent() {
  useGoBack(null);
  const { t } = useTranslation();
  const { showConfirmDialog } = useUiStore();

  // State for course filter
  const [selectedCourse, setSelectedCourse] = useState<CourseOption | null>(null);

  const { watch, register, setValue } = useForm<AvatarFilterFormValues>({
    defaultValues: {
      searchText: "",
    },
  });
  const searchTerm = useDebounce(watch("searchText"), 300);

  const {
    data: avatarData,
    isSuccess,
    isPending,
    isFetching,
  } = useQuery(
    allAvatarsQueryOptions(1, avatars_per_query, {
      courseId: selectedCourse?.id ?? undefined,
    }),
  );

  // Load courses for the filter with search functionality
  const { data: coursesData } = useQuery({
    ...allCoursesQueryOptions({ pageSize: 20, pageNumber: 1, searchTerm }),
    placeholderData: keepPreviousData,
  });

  // Prepare course options for the autocomplete
  const courseOptions = useMemo(() => {
    const courseList = coursesData?.items || [];
    return courseList.map((course) => ({ id: course.id, title: course.title }));
  }, [coursesData]);

  const onDeleteAvatar = useCallback((avatar: Avatar) => {
    showConfirmDialog({
      title: "Confirmation.DeleteAvatar.Title",
      description: (
        <>
          <p className="text-foreground-secondary font-medium">
            {t("Confirmation.DeleteAvatar.Description")}
          </p>
          <Img src={avatar.url} height={160} />
        </>
      ),
    });
  }, []);

  const { onBlur: searchTextOnBlur, ...searchTextRegister } = register("searchText");

  if (isPending && !coursesData?.totalCount) return <LoadingScreen />;

  if (isSuccess && !avatarData.totalCount) return <NoAvatarsFound />;

  return (
    <PageMainContent ps="medium"  className="pb-0">

      <div className="sticky top-0 z-5">
        <div className="bg-background ">
          <AvatarLegend />
        </div>
        <div className="bg-background h-1.5"></div>
        <div className="from-background h-1.5 bg-linear-to-b to-transparent" />
      </div>

      {/* Course Filter */}
      <div className="mt-2.5 mb-0.5">
        <FieldLabel>{t("MarketingKit.SelectCourse")}</FieldLabel>
        <Autocomplete
          value={selectedCourse}
          onChange={(newValue) => setSelectedCourse(newValue)}
          options={courseOptions}
          getOptionLabel={(option) => option.title}
          getOptionKey={(option) => option.id.toString()}
          onSearch={(s) => setValue("searchText", s)}
          placeholder={t("Courses.AllCourses")}
          isNullable
        />
      </div>

      {isPending || isFetching ? (
        <LoadingScreen />
      ) : (
        <div className="xxl:grid-cols-6 mt-11.25 grid grid-cols-1 gap-x-15 gap-y-6.75 sm:grid-cols-2 lg:grid-cols-4">
          {isSuccess &&
            avatarData.items?.map((avatar) => (
              <div key={avatar.id}>
                <AvatarGridItem
                  onDelete={() => onDeleteAvatar(avatar)}
                  image={avatar.url}
                  name={"Avatar Name"}
                />
              </div>
            ))}
        </div>
      )}
    </PageMainContent>
  );
}
